import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import ScrollProgress from '../components/ui/ScrollProgress';

type TimelineEntry = {
  year: string;
  title: string;
  tags: string[];
};

function EngineeringTimelinePage() {
  const timeline = (setup as { engineeringTimeline?: TimelineEntry[] }).engineeringTimeline || [];
  const [mode, setMode] = useState<string>(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme', mode);
  }, [mode]);

  return (
    <div className={`main-container min-h-screen ${mode === 'dark' ? 'dark-mode dark' : 'light-mode'}`}>
      <ScrollProgress />
      <header className="border-b border-line bg-canvas/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="ui-btn-ghost !px-2 !py-1.5 text-sm inline-flex items-center gap-1.5">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Portfolio
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMode((m) => (m === 'dark' ? 'light' : 'dark'))}
              className="ui-btn-ghost !px-2 !py-1.5"
              aria-label="Toggle theme"
            >
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </button>
            <Link to="/resume" className="ui-btn-secondary !text-xs !px-3 !py-1.5">
              Resume
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20">
        <p className="ui-eyebrow mb-4">Career progression</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-ink mb-4">
          Engineering Timeline
        </h1>
        <p className="text-ink-muted leading-relaxed mb-12 max-w-2xl">
          From embedded systems and Android development to enterprise SaaS, cloud infrastructure, and AI automation—continuous growth across domains.
        </p>

        <div className="relative">
          <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-px bg-line-strong" aria-hidden />

          <div className="space-y-0">
            {timeline.map((entry, index) => (
              <motion.div
                key={`${entry.year}-${entry.title}`}
                initial={{ opacity: 0, x: -16, filter: 'blur(4px)' }}
                whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className="relative pl-14 sm:pl-16 pb-10"
              >
                <div className="absolute left-2.5 sm:left-4 top-2 w-4 h-4 rounded-full bg-accent border-[3px] border-canvas z-10" />
                <p className="text-sm font-bold text-accent tabular-nums mb-1">{entry.year}</p>
                <h2 className="text-lg font-semibold text-ink mb-3 tracking-[-0.01em]">{entry.title}</h2>
                <div className="flex flex-wrap gap-2">
                  {entry.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-medium text-ink-secondary px-2.5 py-1 rounded-md bg-canvas-muted border border-line"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                {index < timeline.length - 1 && (
                  <span className="block text-ink-faint text-sm mt-6 pl-1" aria-hidden>↓</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default EngineeringTimelinePage;
